import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface ContentGoal {
  id: string;
  name: string;
  emoji: string;
  description: string;
}

interface ContentGoalSelectProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export const ContentGoalSelect: React.FC<ContentGoalSelectProps> = ({
  value,
  onChange,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t, i18n } = useTranslation();
  
  // Define content goals with translations
  const contentGoals = React.useMemo(() => {
    return [
      {
        id: 'educate',
        name: t('contentCreation.form.goals.educate', 'Educate'),
        emoji: '📚',
        description: t('contentCreation.form.goalDescriptions.educate', 'Educational content that teaches and shares knowledge with your audience')
      },
      {
        id: 'entertain',
        name: t('contentCreation.form.goals.entertain', 'Entertain'),
        emoji: '🎭',
        description: t('contentCreation.form.goalDescriptions.entertain', 'Entertaining content that captivates and engages through humor, stories, or creativity')
      },
      {
        id: 'inform',
        name: t('contentCreation.form.goals.inform', 'Inform'),
        emoji: '📢',
        description: t('contentCreation.form.goalDescriptions.inform', 'Informative content that shares news, updates, or important information')
      },
      {
        id: 'promote',
        name: t('contentCreation.form.goals.promote', 'Promote'),
        emoji: '🚀',
        description: t('contentCreation.form.goalDescriptions.promote', 'Promotional content that highlights products, services, or campaigns')
      }
    ];
  }, [t, i18n.language]);
  
  const selectedGoal = contentGoals.find(goal => goal.id === value) || null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-left ${className}`}
      >
        <span className="flex items-center gap-2">
          {selectedGoal ? (
            <>
              <span className="text-lg">{selectedGoal.emoji}</span>
              <span className="text-gray-700">{selectedGoal.name}</span>
            </>
          ) : (
            <span className="text-gray-500">{t('contentCreation.form.selectGoal', 'Select a goal')}</span>
          )}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-500" />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
          />
          <div className="absolute left-0 right-0 mt-2 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-20">
            <div className="py-1">
              {contentGoals.map(goal => (
                <button
                  type="button"
                  key={goal.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(goal.id);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 ${
                    goal.id === value
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-700 hover:text-primary-600'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{goal.emoji}</span>
                    <div>
                      <div className="font-medium">{goal.name}</div>
                      <div className="text-xs text-gray-500">{goal.description}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

